import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useEventListener } from 'usehooks-ts';

const getGenericKeys = <T extends {}>(obj?: T) =>
	Object.keys(obj || {}) as Array<keyof T>;

const useKeyboard = (
	key: string,
	callback: () => void,
	keyboardOptions?: Partial<
		Pick<KeyboardEvent, 'altKey' | 'metaKey' | 'shiftKey' | 'ctrlKey'>
	> | null,
	options?: { forceAllModifiers?: boolean; disabled?: boolean }
) => {
	const callbackRef = useRef(callback);
	useEffect(() => {
		callbackRef.current = callback;
	}, [callback]);

	const onKeyboardButton = useCallback(
		(e: KeyboardEvent) => {
			const isValid =
				!options?.disabled &&
				(!keyboardOptions ||
					(options?.forceAllModifiers
						? getGenericKeys(keyboardOptions).every(
								(k) => keyboardOptions![k] === e[k]
						  )
						: getGenericKeys(keyboardOptions).some(
								(k) => keyboardOptions![k] === e[k]
						  )));
			if (isValid && e.key === key) {
				if (e.target instanceof HTMLInputElement && e.key === 'Backspace')
					return;
				e.preventDefault();
				callbackRef.current();
			}
		},
		[options, keyboardOptions, callbackRef, key]
	);

	useEventListener('keydown', onKeyboardButton);
};

const useEditorKeyboard = (onSave: () => void) => {
	const options = useMemo<Parameters<typeof useKeyboard>[2]>(
		() => ({
			ctrlKey: true,
			metaKey: true
		}),
		[]
	);
	useKeyboard('s', onSave, options);
};

export default useKeyboard;
export { useEditorKeyboard };
